import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Body1, Button, Caption1, Input, Select, Spinner, Switch, makeStyles, tokens } from "@fluentui/react-components";
import { ArrowClockwise24Regular, Search20Regular } from "@fluentui/react-icons";
import { TIPI, type Tipo } from "@app/schema";
import { useData } from "../../data/DataProvider";
import { useCatalogo } from "../../data/catalogo";
import { FILTRI_STATO, loadCoda, loadMieiTipi, ordinaCoda, type FiltriCoda, type FiltroStato, type Richiesta } from "../../data/richieste";
import { formatEuro } from "../../lib/format";
import { navigate } from "../../lib/nav";
import { storage } from "../../lib/storage";
import { Shell } from "../../components/Shell";
import { ChipGroup, ChipMulti } from "../../components/ChipGroup";
import { RichiestaCard } from "../../components/RichiestaCard";
import { ErrorState } from "../../components/ErrorState";

const useStyles = makeStyles({
  filters: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS },
  row: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalM, flexWrap: "wrap" },
  select: { minWidth: "180px", flexGrow: 1, maxWidth: "320px" },
  count: { color: tokens.colorNeutralForeground3 },
  empty: { textAlign: "center", color: tokens.colorNeutralForeground2, padding: tokens.spacingVerticalXXL },
});

const STATI_FILTRO = Object.keys(FILTRI_STATO) as FiltroStato[];

/** Filtri salvati sul dispositivo; i tipi, al primo accesso, vengono dall'elenco Gestori acquisti. */
interface FiltriSalvati extends FiltriCoda {
  extraBudget: boolean;
}

function filtriSalvati(): FiltriSalvati | undefined {
  try {
    const f = JSON.parse(storage.get("filtriCoda") ?? "") as FiltriSalvati;
    return { ...f, tipi: f.tipi.filter((t) => (TIPI as readonly string[]).includes(t)) };
  } catch {
    return undefined;
  }
}

export function Coda({ switcher }: { switcher?: ReactNode }) {
  const s = useStyles();
  const data = useData();
  const { catalogo } = useCatalogo(data);
  const [filtri, setFiltri] = useState<FiltriSalvati | undefined>(filtriSalvati);
  const [testo, setTesto] = useState("");
  const [items, setItems] = useState<Richiesta[]>();
  const [nextLink, setNextLink] = useState<string>();
  const [error, setError] = useState<unknown>();

  // Primo accesso: tipi assegnati all'utente in Gestori acquisti (nessuno = tutti)
  useEffect(() => {
    if (filtri) return;
    void loadMieiTipi(data).then((tipi) => setFiltri({ stato: "Da gestire", tipi, extraBudget: false }));
  }, [data, filtri]);

  const aggiorna = (patch: Partial<FiltriSalvati>) =>
    setFiltri((f) => {
      const next = { ...(f ?? { stato: "Da gestire", tipi: [], extraBudget: false }), ...patch };
      storage.set("filtriCoda", JSON.stringify(next));
      return next;
    });

  // Stato, tipi e servizio si filtrano su SharePoint (colonne indicizzate)
  const stato = filtri?.stato;
  const tipiKey = filtri?.tipi.join("|");
  const servizio = filtri?.servizio;
  const load = useCallback(async () => {
    if (!stato) return;
    setError(undefined);
    setItems(undefined);
    try {
      const page = await loadCoda(data, { stato, tipi: tipiKey ? (tipiKey.split("|") as Tipo[]) : [], servizio });
      setItems(page.items);
      setNextLink(page.nextLink);
    } catch (e) {
      setError(e);
    }
  }, [data, stato, tipiKey, servizio]);

  useEffect(() => {
    void load();
  }, [load]);

  const more = async () => {
    if (!filtri) return;
    const page = await loadCoda(data, filtri, nextLink);
    setItems((p) => [...(p ?? []), ...page.items]);
    setNextLink(page.nextLink);
  };

  // Extra budget e ricerca si applicano alle richieste caricate
  const visibili = useMemo(() => {
    if (!items || !filtri) return undefined;
    const t = testo.trim().toLowerCase();
    let list = filtri.extraBudget ? items.filter((r) => r.fields.ExtraBudget) : items;
    if (t)
      list = list.filter((r) =>
        [r.fields.Title, r.fields.Descrizione, r.fields.Codice, r.fields.Categoria, r.fields.Servizio, r.createdBy?.displayName, String(r.id)]
          .filter(Boolean)
          .some((v) => v!.toLowerCase().includes(t)),
      );
    return ordinaCoda(list);
  }, [items, filtri, testo]);

  const totale = useMemo(() => visibili?.reduce((sum, r) => sum + (r.fields.Valore ?? 0), 0) ?? 0, [visibili]);

  return (
    <Shell
      title="Richieste di acquisto"
      actions={
        <>
          {switcher}
          <Button appearance="subtle" icon={<ArrowClockwise24Regular />} aria-label="Aggiorna" onClick={load} />
        </>
      }
    >
      {filtri && (
        <div className={s.filters}>
          <ChipGroup ariaLabel="Filtro stato" options={STATI_FILTRO} value={filtri.stato} onChange={(stato) => aggiorna({ stato })} />
          <ChipMulti ariaLabel="Filtro tipo (nessuno = tutti)" options={TIPI} value={filtri.tipi} onChange={(tipi) => aggiorna({ tipi })} />
          <div className={s.row}>
            <Select
              className={s.select}
              aria-label="Servizio"
              value={filtri.servizio ?? ""}
              onChange={(_, d) => aggiorna({ servizio: d.value || undefined })}
            >
              <option value="">Tutti i servizi</option>
              {(catalogo?.servizi ?? (filtri.servizio ? [filtri.servizio] : [])).map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </Select>
            <Switch label="Solo extra budget" checked={filtri.extraBudget} onChange={(_, d) => aggiorna({ extraBudget: d.checked })} />
          </div>
          <Input
            size="large"
            contentBefore={<Search20Regular />}
            placeholder="Cerca per articolo, codice, richiedente…"
            value={testo}
            onChange={(_, d) => setTesto(d.value)}
          />
        </div>
      )}

      {error ? (
        <ErrorState error={error} />
      ) : !visibili ? (
        <Spinner style={{ marginTop: 48 }} />
      ) : visibili.length === 0 ? (
        <Body1 className={s.empty}>
          {testo || filtri?.extraBudget ? "Nessuna richiesta corrisponde ai filtri." : "Nessuna richiesta in questo stato."}
        </Body1>
      ) : (
        <>
          <Caption1 className={s.count}>
            {visibili.length} {visibili.length === 1 ? "richiesta" : "richieste"}
            {nextLink ? " caricate" : ""}
            {totale > 0 ? ` · ${formatEuro(totale)} + IVA` : ""}
          </Caption1>
          {visibili.map((r) => (
            <RichiestaCard key={r.id} r={r} showRichiedente onOpen={() => navigate(`/r/${r.id}`)} />
          ))}
          {nextLink && <Button onClick={more}>Mostra altre</Button>}
        </>
      )}
    </Shell>
  );
}
